"use client"
import React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { 
  Calendar, ShoppingCart, Package, 
  CreditCard, TrendingUp, TrendingDown, Coins, Wallet
} from "lucide-react"
import {
  BarChart as RechartsBarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart as RechartsLineChart, // Fixed: Import LineChart as RechartsLineChart
} from "recharts"
import jsPDF from "jspdf"
import "jspdf-autotable"
import html2canvas from "html2canvas"
import { pdf } from "@react-pdf/renderer"

import "./Performance.css"
import { useOrdersContext } from "../../contexts/OrdersContext"
import { formatPrice } from "../../MesPages/Panier/Cart"
import { formatDateOriginal } from "../../services/apiService"
import { generatePageNumbers } from "../../services/lib"
import { useQuery } from "@tanstack/react-query"
import { crudData } from "../../services/apiService"
import { toast } from "react-toastify"
import { Modal, Button } from "react-bootstrap"
import { CSVLink } from "react-csv"
import InvoicePDF from "../../Mescomposants/exportPDF/InvoicePDF"
import OrderForm from "../../Mescomposants/exportExcel/exportExcel"
import { useNavigate, useSearchParams } from "react-router-dom"

/**
 * Composant de tableau de bord de performance client
 * Affiche les statistiques, commandes, factures et produits consultés
 */
const MONTH_NAMES = [
  "Janvier","Février","Mars","Avril","Mai","Juin",
  "Juillet","Août","Septembre","Octobre","Novembre","Décembre"
]
  

const Performance = () => {

  // 1. Tous les useState -----------------

  // Filtres commandes
  const [filterYear, setFilterYear] = useState("")   // vide = année en cours affichée
  const [chartHovered, setChartHovered] = useState(false)
  const [montantPanier, setMontantPanier] = useState(0)
  const [filterType, setFilterType] = useState("")
  // const [clientPlafond, setClientPlafond] = useState(() => {
  //     const ud = typeof localStorage !== "undefined"
  //         ? JSON.parse(localStorage.getItem("userData") || '{}')
  //         : {}
  //     return Number(ud.STR_SOCSOLDE) || 0
  // })  
  // const chartRef = useRef(null)
  // ajouté 22/04/2026
  // États pour la comparaison des dépenses
  const [compareYearStart, setCompareYearStart] = useState(new Date().getFullYear() - 1)
  const [compareYearEnd, setCompareYearEnd] = useState(new Date().getFullYear())
  // États principaux pour le contrôle du dashboard
  // Gestion de l'affichage, des filtres et de l'exportation
  const [dateRange, setDateRange] = useState("3mois")
  const [activeTab, setActiveTab] = useState("commandes")
  const [isLoading, setIsLoading] = useState(true)
  const [animateCharts, setAnimateCharts] = useState(false)
  //const [searchTerm, setSearchTerm] = useState("")
  const [showCalendar, setShowCalendar] = useState(false)
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 3)))
  const [endDate, setEndDate] = useState(new Date())
  const [showFilters, setShowFilters] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const calendarRef = useRef(null)
  const filterRef = useRef(null)
  const dashboardRef = useRef(null)
  const chartRef = useRef(null)

  // États spécifiques pour la gestion des commandes
  
  const [year, setYear] = useState(new Date().getFullYear()) // graphique selon année en cours
  const [orderYear, setOrderYear] = useState("all") // Toutes les années par defaut pour le tableau des commandes
  const [orders, setOrders] = useState([])
  const [products, setProducts] = useState([])
  const [currentOrderID, setCurrentOrderID] = useState(null)
  const [isLoadingProduct, setIsLoadingProduct] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [pageNumbers, setPageNumbers] = useState([])
  const [totalPage, setTotalPage] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemPerPage = 10
  const [currentProducts, setCurrentProducts] = useState([])
  const indexOfLastItem = currentPage * itemPerPage
  const indexOfFirstItem = indexOfLastItem - itemPerPage
  // const [chartHovered, setChartHovered] = useState(false)

  // États pour la section des produits les plus consultés
  // Gestion de la pagination, recherche et filtrage par mois
  const [productLimit, setProductLimit] = useState(10)
  const [productPage, setProductPage] = useState(1)
  const [productSearch, setProductSearch] = useState()
  const [productMonth, setProductMonth] = useState()
  const [productTotal, setProductTotal] = useState()
  const [productTotalPage, setProductTotalPage] = useState()
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const [invoiceActiveFilters, setInvoiceActiveFilters] = useState(() => {
    const filters = {
      year: "",
      statut: "",
      search: "",
    }
    searchParams.forEach((value, key) => {
      filters[key] = value
    })
    delete filters.limit
    delete filters.page
    return filters
  })

  const [invoicePage, setInvoicePage] = useState(() => {
    return Number.parseInt(searchParams.get("page")) || 1
  })

  const [invoiceLimit, setInvoiceLimit] = useState(() => {
    return Number.parseInt(searchParams.get("limit")) || 20
  })
  
  
  const [invoiceTotalPage, setInvoiceTotalPage] = useState(0)
  const [invoicePageNumbers, setInvoicePageNumbers] = useState([])
  const handleChartMouseEnter = () => setChartHovered(true)
  const handleChartMouseLeave = () => setChartHovered(false)
  const handleChangeYear = (yr) => setYear(yr)

  // Fonction pour récupérer les statistiques des commandes (intégration de OrderChart.js)
  const fetchStats = async (yr) => {
    try {
        const ud = JSON.parse(localStorage.getItem("userData"))
        const response = await crudData(
            {
                mode: "statOrders",
                YEAR: yr,
                PCVGCLIID: ud.LG_CLIID,
                STR_COMMSOURCE: filterType || null,
            },
            `StatistiqueManager.php`,
        )
        
        if (response.data.code_statut === "1") {
            return response.data.data || []
        } 
        return []
      
    } catch (error) {
        console.error("fetchStats erreur:", error)
        return []
    }
}

// ── Récupérer le montant du panier ───────────────────────────
  // const fetchMontantPanier = async () => {
  //     try {
  //         const ud = JSON.parse(localStorage.getItem("userData"))
  //         const apiEndpointe = JSON.parse(localStorage.getItem("apiEndpointe"))
  //         const appMode = JSON.parse(localStorage.getItem("appMode"))

  //         const response = await crudData(
  //             {
  //                 mode: appMode.getClientPanierMode,
  //                 LG_AGEID: ud.LG_AGEID,
  //                 STR_UTITOKEN: ud.STR_UTITOKEN,
  //             },
  //             apiEndpointe.CommandeManagerEndPoint
  //         )

  //         console.log("Réponse getClientPanier:", response.data)

  //         if (response.data.code_statut === "1") {
  //             const panier = response.data.data
  //             const montant = Number(panier?.dbl_commmtttc) || 0
  //             console.log("setMontantPanier appelé avec:", montant) // ← ce log apparaît-il ?
  //             setMontantPanier(montant)
  //         } else {
  //             // ← Pas de panier en cours
  //             setMontantPanier(0)
  //         }
  //     } catch (error) {
  //         console.error("Erreur fetchMontantPanier:", error)
  //         setMontantPanier(0)
  //     }
  // }

  // 2. Tous les useQuery statsData -----------------
  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ["stats", year, filterType],
    queryFn: () => fetchStats(year),
    placeholderData: [],
    keepPreviousData: true,
    // Ne charger que si on est sur l'onglet commandes
    enabled: activeTab === "commandes",
  })


  // 3. filteredChartData — utilise statsData -----------------
  const filteredChartData = useMemo(() => {
    if (!statsData || !Array.isArray(statsData) || statsData.length === 0) {
        console.log("statsData vide ou null:", statsData)
        return []
    }
    // console.log("statsData dans useMemo:", statsData)

    const data = statsData.map(stat => ({
        name: MONTH_NAMES[(Number(stat.MONTH) - 1)],
        month: Number(stat.MONTH),
        commandes: Number(stat.NB_ORDER),
        revenues: Number(stat.TOTAL_AMOUNT),
    }))

    console.log("data mappée:", data)

    // Filtre par mois seulement si un mois est sélectionné
    // if (filterMonth && filterMonth !== "") {
    //     return data.filter(d => String(d.month).padStart(2, "0") === filterMonth)
    // }

    return data // ← retourner toutes les données si pas de filtre mois
  }, [statsData])
  // console.log("filteredChartData final:", filteredChartData)
  // console.log("statsData final:", statsData)

  // ── 4. useQuery invoices ──────────────────────────────────────
  const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ["invoices", invoiceActiveFilters, invoicePage, invoiceLimit, activeTab],
    queryFn: () => fetchInvoices(invoiceActiveFilters),
    placeholderData: [],
    keepPreviousData: true,
    // ← Ne charger que si on est sur les onglets qui ont besoin des factures
    enabled: activeTab === "factures" || activeTab === "depenses",
  })

  const fetchCompareExpenses = async (yearStart, yearEnd) => {
    try {
        const ud = JSON.parse(localStorage.getItem("userData"))
        const response = await crudData(
            {
                mode: "compareExpenses",
                YEAR_START: yearStart,
                YEAR_END: yearEnd,
                PCVGCLIID: ud.LG_CLIID,
            },
            `StatistiqueManager.php`,
        )
        if (response.data.code_statut === "1") {
            return response.data.data || []
        }
        return []
    } catch (error) {
        console.error("fetchCompareExpenses erreur:", error)
        return []
    }
  }
  // ── useQuery comparaison ──────────────────────────────────────
  const { data: compareExpensesData, isLoading: isLoadingCompare } = useQuery({
      queryKey: ["compareExpenses", compareYearStart, compareYearEnd],
      queryFn: () => fetchCompareExpenses(compareYearStart, compareYearEnd),
      placeholderData: [],
      keepPreviousData: true,
      enabled: activeTab === "depenses",
  })

  // ── 5. compareData ───────────
  // Données pour la comparaison des dépenses
  const compareData = compareExpensesData || []
  // const compareData = React.useMemo(() => {
  //   const months = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"]
    
  //   if (!invoices || invoices.length === 0) {
  //       // Données de démonstration si pas de factures
  //       return months.map((name, i) => ({
  //           name,
  //           startYear: Math.floor(Math.random() * 5000000) + 1000000,
  //           endYear: Math.floor(Math.random() * 5000000) + 1000000,
  //       }))
  //   }

  //   return months.map((name, i) => {
  //       const month = i + 1
  //       const startTotal = invoices
  //           .filter(inv => {
  //               const d = new Date(inv.PcvDate)
  //               return d.getFullYear() === compareYearStart && d.getMonth() + 1 === month
  //           })
  //           .reduce((acc, inv) => acc + Number(inv.PcvMtTTC || 0), 0)

  //       const endTotal = invoices
  //           .filter(inv => {
  //               const d = new Date(inv.PcvDate)
  //               return d.getFullYear() === compareYearEnd && d.getMonth() + 1 === month
  //           })
  //           .reduce((acc, inv) => acc + Number(inv.PcvMtTTC || 0), 0)

  //       return { name, startYear: startTotal, endYear: endTotal }
  //   })
  // }, [invoices, compareYearStart, compareYearEnd])

  // ── 6. useQuery productsStats ───────────
  const { data: productsStats } = useQuery({
    queryKey: ["productsStats", productLimit, productPage, productSearch, productMonth],
    queryFn: () => fetchProductStats(productLimit, productPage, productSearch, productMonth),
    placeholderData: { data: [] },
    keepPreviousData: true,
  })

  // ── 7. Tous les useEffect ──────
  // Fermer les popups quand on clique ailleurs
  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false)
      }
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Simuler un chargement
  useEffect(() => {
    const timer = setTimeout(() => {
        setIsLoading(false)
        setTimeout(() => setAnimateCharts(true), 300)
    }, 1000)

     // Ajouter ici le fetch du panier
    const ud = typeof localStorage !== "undefined"
        ? JSON.parse(localStorage.getItem("userData"))
        : null

    if (ud && ud.LG_AGEID) {
      const apiEndpointe = JSON.parse(localStorage.getItem("apiEndpointe"))
      const appMode = JSON.parse(localStorage.getItem("appMode"))

      // Fetch sumAmountOrders
      if (typeof fetchUserSumAmountOrders === "function") {
          fetchUserSumAmountOrders()
      }

      // Fetch montant panier
      crudData(
          {
              mode: appMode.getClientPanierMode,
              LG_AGEID: ud.LG_AGEID,
              STR_UTITOKEN: ud.STR_UTITOKEN,
          },
          apiEndpointe.CommandeManagerEndPoint
      ).then((response) => {

          console.log("Réponse panier:", response.data)
          const montant = Number(response.data?.data?.dbl_commmtttc) || 0
          console.log("Montant panier:", montant)
          setMontantPanier(montant)
      }).catch((err) => {
          console.error("Erreur panier:", err)
      })
      // ── Fetch plafond client ──
      // D'abord essayer STR_SOCSOLDE depuis userData (déjà récupéré au login)
      const plafondFromLogin = Number(ud.STR_SOCSOLDE) || 0
      
      if (plafondFromLogin > 0) {
          // Plafond déjà dans userData utiliser directement
          console.log("Plafond depuis userData:", plafondFromLogin)
          // setClientPlafond(plafondFromLogin)
      } else {
          // Plafond absent appeler l'ERP
          crudData(
              {
                  mode: "getClientPlafond",
                  LG_CLIID: ud.LG_CLIID,
                  STR_UTITOKEN: ud.STR_UTITOKEN,
              },
              apiEndpointe.CommandeManagerEndPoint
          ).then((response) => {
              console.log("Réponse plafond ERP:", response.data)
              const plafond = Number(
                  response.data?.clients?.[0]?.CliPlaf ||
                  response.data?.data?.CliPlaf ||
                  0
              )
              console.log("Plafond depuis ERP:", plafond)
              // setClientPlafond(plafond)
          }).catch((err) => {
              console.error("Erreur plafond:", err)
              // setClientPlafond(0)
          })
      }
    }

    return () => clearTimeout(timer)
  }, [])

  // Mettre à jour les pageNumbers pour la pagination des produits
  useEffect(() => {
    if (productsStats !== null && productsStats !== undefined) {
      setProductTotal(productsStats.total)
      setProductTotalPage(() => {
        return Math.ceil(productsStats.total / productLimit)
      })
      setPageNumbers(generatePageNumbers(productTotalPage, productPage))
    }
  }, [productsStats, productPage, productTotalPage, productLimit])

  // useEffect(() => {
  //   const user = typeof localStorage !== "undefined" ? JSON.parse(localStorage.getItem("userData")) : null
  //   if (user && user.LG_CLIID && typeof fetchUserSumAmountOrders === "function") {
  //     fetchUserSumAmountOrders()
  //   }
  // }, [])
//   useEffect(() => {
//     const ud = typeof localStorage !== "undefined" 
//         ? JSON.parse(localStorage.getItem("userData")) 
//         : null
    
//     if (!ud || !ud.LG_AGEID) return
//     console.log("useEffect montage — appel fetchMontantPanier")

//     if (typeof fetchUserSumAmountOrders === "function") {
//         fetchUserSumAmountOrders()
//     }
    
//     fetchMontantPanier()
// }, [])
// useEffect(() => {
//     const ud = typeof localStorage !== "undefined"
//         ? JSON.parse(localStorage.getItem("userData"))
//         : null

//     if (!ud || !ud.LG_AGEID) return

//     console.log("useEffect montage OK") // ← test

//     // ── Fetch sumAmountOrders ──
//     if (typeof fetchUserSumAmountOrders === "function") {
//         fetchUserSumAmountOrders()
//     }

//     // ── Fetch montant panier directement ici ──
//     const apiEndpointe = JSON.parse(localStorage.getItem("apiEndpointe"))
//     const appMode = JSON.parse(localStorage.getItem("appMode"))

//     crudData(
//         {
//             mode: appMode.getClientPanierMode,
//             LG_AGEID: ud.LG_AGEID,
//             STR_UTITOKEN: ud.STR_UTITOKEN,
//         },
//         apiEndpointe.CommandeManagerEndPoint
//     ).then((response) => {
//         console.log("Panier réponse:", response.data)
//         if (response.data.code_statut === "1") {
//             const montant = Number(response.data.data?.dbl_commmtttc) || 0
//             console.log("Montant panier:", montant)
//             setMontantPanier(montant)
//         }
//     }).catch((err) => {
//         console.error("Erreur panier:", err)
//         setMontantPanier(0)
//     })

// }, []) // ← une seule fois au montage

  // Fonctions pour la partie commandes (intégration de OrderChart.js et Commandes.js)
  useEffect(() => {
    setCurrentProducts(products.slice(indexOfFirstItem, indexOfLastItem))
  }, [currentPage, products, indexOfFirstItem, indexOfLastItem])

  useEffect(() => {
    const apiEndpointe = JSON.parse(localStorage.getItem("apiEndpointe"))
    const mode = JSON.parse(localStorage.getItem("appMode"))

    if (currentOrderID !== null) {
      const params = {
        mode: mode.listProductByCommandeMode,
        LG_COMMID: currentOrderID,
      }
      fetchProductsOnOrder(params, apiEndpointe.CommandeManagerEndPoint)
    }
  }, [currentOrderID])

  useEffect(() => {
    if (activeTab !== "mescommandes") return

    const apiEndpointe = JSON.parse(localStorage.getItem("apiEndpointe"))
    const mode = JSON.parse(localStorage.getItem("appMode"))
    const userData = JSON.parse(localStorage.getItem("userData"))

    const params = {
      mode: mode.listClientCommandeMode,
      "FILTER_OPTIONS[PcvGCliID]": userData.LG_CLIID,
      PAGE: page,
      LIMIT: limit,
    }

    if (orderYear  !== null && orderYear  !== "all" && orderYear  !== "") {
      params["FILTER_OPTIONS[PcvDate]"] = orderYear 
    }

    fetchOrders(params, apiEndpointe.CommandeManagerEndPoint)
  }, [orderYear, page, limit, activeTab])

  useEffect(() => {
    setPageNumbers(generatePageNumbers(totalPage, page))
  }, [page, totalPage])

  useEffect(() => {
    if (orders.length === 0) {
      setProducts([])
    }
  }, [orders])

  useEffect(() => {
    const updatedFilters = {
      year: "",
      statut: "",
      search: "",
    }

    searchParams.forEach((value, key) => {
      updatedFilters[key] = value
    })

    setInvoiceActiveFilters(updatedFilters)
  }, [searchParams])

  useEffect(() => {
    setInvoicePageNumbers(generatePageNumbers(invoiceTotalPage, invoicePage))
  }, [invoicePage, invoiceTotalPage])

  

  // Objectifs annuels de commandes
  const annualTargets = {
    2020: 3000000,
    2021: 3500000,
    2022: 4000000,
    2023: 4500000,
    2024: 5000000,
    2025: 5500000,
  }

  

  // Données pour les dépenses annuelles
  const annualExpensesData = [
    { name: "Janvier", 2023: 5000000, 2024: 4900000, commandes: 5 },
    { name: "Février", 2023: 3200000, 2024: 3100000, commandes: 4 },
    { name: "Mars", 2023: 7000000, 2024: 7100000, commandes: 8 },
    { name: "Avril", 2023: 5200000, 2024: 5100000, commandes: 5 },
    { name: "Mai", 2023: 4000000, 2024: 4100000, commandes: 2 },
    { name: "Juin", 2023: 4500000, 2024: 4400000, commandes: 3 },
    { name: "Juillet", 2023: 7800000, 2024: 7700000, commandes: 4 },
    { name: "Août", 2023: 5600000, 2024: 5700000, commandes: 5 },
    { name: "Septembre", 2023: 5700000, 2024: 5800000, commandes: 4 },
    { name: "Octobre", 2023: 5400000, 2024: 5500000, commandes: 4 },
    { name: "Novembre", 2023: 7000000, 2024: 6900000, commandes: 8 },
    { name: "Décembre", 2023: 3500000, 2024: 3600000, commandes: 5 },
  ]

  // Données pour les produits les plus consultés
  const mostViewedProducts = [
    { name: "Vaccin Canin", views: 1253, percentage: 22 },
    { name: "Antibiotique Bovin", views: 982, percentage: 17 },
    { name: "Complément Alimentaire", views: 845, percentage: 15 },
    { name: "Antiparasitaire", views: 756, percentage: 13 },
    { name: "Matériel Chirurgical", views: 689, percentage: 12 },
    { name: "Autres", views: 1200, percentage: 21 },
  ]

  // Données pour les produits les plus commandés
  const mostOrderedProducts = [
    { name: "Vaccin Canin", orders: 325, amount: 32500 },
    { name: "Antibiotique Bovin", orders: 287, amount: 28700 },
    { name: "Complément Alimentaire", orders: 245, amount: 24500 },
    { name: "Antiparasitaire", orders: 198, amount: 19800 },
    { name: "Matériel Chirurgical", orders: 176, amount: 17600 },
  ]

  // Couleurs pour les graphiques (basées sur l'image)
  const COLORS = ["#3056a2", "#5b7bc0", "#8599d1", "#a8b5e0", "#ccd2ef", "#e6eaf7"]
  const COLORS_COMPARISON = ["#3056a2", "#5b7bc0"]

  // Statistiques générales commande récupérées du contexte
  const { sumAmountOrders, fetchUserSumAmountOrders } = useOrdersContext()
  const userData =
    typeof localStorage !== "undefined"
      ? JSON.parse(localStorage.getItem("userData") || '{"STR_SOCSOLDE": 0}')
      : { STR_SOCSOLDE: 0 }
  // Ajoute ce log juste après userData
  console.log("userData complet:", userData)
  console.log("STR_SOCSOLDE:", userData?.STR_SOCSOLDE)
  console.log("montantPanier state:", montantPanier)
  console.log("sumAmountOrders:", sumAmountOrders)
  const clisolde = Number(userData.STR_SOCSOLDE) || 0

  // Récupérer l'objectif pour l'année actuelle
  const currentTarget = annualTargets[year] || 5000000

  // Calculer le pourcentage d'avancement vers l'objectif
  const calculateProgress = (amount, target) => {
    return Math.min(100, Math.round((amount / target) * 100))
  }

  // Déterminer la couleur de la barre de progression en fonction du pourcentage
  const getProgressColor = (percentage) => {
    if (percentage < 30) return "#dc3545" // Rouge pour moins de 30%
    if (percentage < 70) return "#fd7e14" // Orange pour moins de 70%
    return "#28a745" // Vert pour 70% et plus
  }

  // logique de calcul de l'encours restant
  const totalAmount = 0 // This should be replaced with your actual cart total if available in this component
  // const encoursRestant = clisolde - totalAmount - (sumAmountOrders || 0)

  // Calculer le pourcentage d'avancement
  const progress = calculateProgress(sumAmountOrders || 0, currentTarget)
  const encoursRestant = clisolde - montantPanier - (sumAmountOrders || 0)
  // Déterminer la couleur de la barre de progression
  const progressColor = getProgressColor(progress)

  // const stats = [
  //   {
  //       title: "Montant total des Commandes",
  //       value: `${formatPrice(sumAmountOrders || 0)} FCFA`,
  //       progress: progress,
  //       progressColor: progressColor,
  //       icon: <Package />,
  //   },
  //   {
  //       title: "Panier actuel",
  //       // ← montantPanier depuis la BD
  //       value: `${montantPanier > 0 ? formatPrice(montantPanier) : "0"} FCFA`,
  //       icon: <ShoppingCart />,
  //   },
  //   {
  //       title: "Encours actuel",
  //       // ← plafond - panier - commandes
  //       value: `${encoursRestant > 0 ? formatPrice(encoursRestant) : "0"} FCFA`,
  //       changeType: "increase",
  //       icon: <CreditCard />,
  //   },
  // ]
  const stats = useMemo(() => {
    const plafond = Number(
        JSON.parse(localStorage.getItem("userData") || '{}')?.STR_SOCSOLDE
    ) || 0
    // const clisolde = Number(userData?.STR_SOCSOLDE) || 0
    const encoursRestant = plafond - montantPanier - (sumAmountOrders || 0)
    console.log("Stats calcul:", { plafond, montantPanier, sumAmountOrders, encoursRestant })
    // Formater l'encours selon le cas
    const encoursDisplay = () => {
        if (plafond === 0) return "Pas d'encours(0 FCFA)"
        if (encoursRestant >= 0) {
            return `${formatPrice(encoursRestant)} FCFA`
        } else {
            // Montant négatif = plafond dépassé
            return `${formatPrice(Math.abs(encoursRestant))} FCFA dépassé`
        }
    }
    // const progress = Math.min(100, Math.round(((sumAmountOrders || 0) / currentTarget) * 100))
    // const progressColor = progress < 30 ? "#dc3545" : progress < 70 ? "#fd7e14" : "#28a745"

    return [
        {
            title: "Montant total des Commandes",
            value: `${formatPrice(sumAmountOrders || 0)} FCFA`,
            progress: Math.min(100, Math.round(((sumAmountOrders || 0) / (currentTarget || 1)) * 100)),
            progressColor: progress < 30 ? "#dc3545" : progress < 70 ? "#fd7e14" : "#28a745",
            icon: <Package />,
        },
        {
            title: "Panier actuel avec TVA",
            value: `${montantPanier > 0 ? formatPrice(montantPanier) : "0"} FCFA`,
            icon: <ShoppingCart />,
        },
        {
            title: "Encours actuel",
            value: encoursDisplay(),
            plafond: plafond > 0 ? `Plafond : ${formatPrice(plafond)} FCFA` : null,
            isOverLimit: encoursRestant < 0,
            icon: <CreditCard />,
        },
      ]
  }, [montantPanier, sumAmountOrders])


  const handleTabChange = (tab) => {
    setActiveTab(tab)
    // Réinitialiser l'animation des graphiques lors du changement d'onglet
    setAnimateCharts(false)
    setTimeout(() => {
      setAnimateCharts(true)
    }, 100)
  }


  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar)
  }

  const handleDownload = (format) => {
    // Logique pour télécharger les données en différents formats (CSV, Excel, etc.)
    console.log(`Téléchargement au format ${format}`)
  }

  const handleViewAllProducts = () => {
    // Logique pour afficher tous les produits
    console.log("Afficher tous les produits")
  }

  // Fonctions pour les produits les plus consultés
  const handleChangeProductLimit = (limit) => {
    setProductLimit(limit)
  }

  const goToProductPage = (page) => {
    setProductPage(page)
  }

  const handleChangeSearch = (value) => {
    setProductSearch(value)
  }

  const handleChangeMonth = (intMonth) => {
    setProductMonth(intMonth)
  }

  /**
   * Récupère les statistiques des produits les plus consultés
   * @param {number} limit - Nombre d'éléments par page
   * @param {number} page - Numéro de page actuel
   * @param {string} search - Terme de recherche (optionnel)
   * @param {string} month - Filtre par mois (optionnel)
   * @returns {Promise} - Données des produits consultés
   */
  const fetchProductStats = async (limit, page, search = null, month = null) => {
    try {
      setIsLoadingProducts(true)
      const response = await crudData(
        {
          mode: "listProductsStatViewed",
          LIMIT: limit,
          PAGE: page,
          "FILTER_OPTIONS[search]": search,
          "FILTER_OPTIONS[month]": month,
        },
        `StatistiqueManager.php`,
      )

      setIsLoadingProducts(false)
      if (response.data.code_statut === "1") {
        return response.data
      } else {
        toast.error(response.data.desc_statut)
        return false
      }
    } catch (error) {
      setIsLoadingProducts(false)
      toast.error("Error: " + error.message)
    }
  }

  /**
   * Génère un rapport PDF du tableau de bord
   * Inclut les statistiques, graphiques et données selon l'onglet actif
   */
  const exportToPDF = async () => {
    setIsExporting(true)

    try {
      // Créer un nouveau document PDF
      const doc = new jsPDF("p", "mm", "a4")

      // Ajouter un titre
      doc.setFontSize(20)
      doc.setTextColor(48, 86, 162) // Couleur primaire
      doc.text("Rapport de Performance", 20, 20)

      // Ajouter la date et période
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.text(`Généré le ${new Date().toLocaleDateString()}`, 20, 30)
      doc.text(`Période: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`, 20, 35)

      // Ajouter les statistiques
      doc.setFontSize(14)
      doc.setTextColor(48, 86, 162)
      doc.text("Statistiques générales", 20, 45)

      // Tableau des statistiques
      // const statsData = stats.map((stat) => [
      //   stat.title,
      //   stat.value,
      //   `${stat.change}% ${stat.changeType === "increase" ? "▲" : "▼"}`,
      // ])

      doc.autoTable({
        startY: 50,
        head: [["Métrique", "Valeur", "Évolution"]],
        body: statsData,
        theme: "grid",
        headStyles: { fillColor: [48, 86, 162] },
      })

      // Ajouter les données selon l'onglet actif
      if (activeTab === "commandes") {
        // Ajouter les données de commandes
        doc.setFontSize(14)
        doc.setTextColor(48, 86, 162)
        doc.text("Commandes", 20, doc.autoTable.previous.finalY + 15)

        const ordersData = orders.map((order) => [
          order.PcvID,
          formatDateOriginal(order.PcvDate),
          `${formatPrice(Number.parseInt(order.PcvMtHT + order.PcvMtHT * 0.05))} FCFA`,
        ])

        doc.autoTable({
          startY: doc.autoTable.previous.finalY + 20,
          head: [["N° Commande", "Date", "Montant (FCFA)"]],
          body: ordersData,
          theme: "grid",
          headStyles: { fillColor: [48, 86, 162] },
        })

        // Capturer le graphique de commandes
        const chartElement = document.querySelector(".chart-content")
        if (chartElement) {
          const canvas = await html2canvas(chartElement)
          const imgData = canvas.toDataURL("image/png")

          // Ajouter le graphique au PDF
          const imgWidth = 170
          const imgHeight = (canvas.height * imgWidth) / canvas.width

          doc.addPage()
          doc.setFontSize(14)
          doc.setTextColor(48, 86, 162)
          doc.text("Graphique des commandes", 20, 20)
          doc.addImage(imgData, "PNG", 20, 30, imgWidth, imgHeight)
        }
      } else if (activeTab === "produits") {
        // Ajouter les produits les plus commandés
        doc.setFontSize(14)
        doc.setTextColor(48, 86, 162)
        doc.text("Produits les plus commandés", 20, doc.autoTable.previous.finalY + 15)

        const productsData = mostOrderedProducts.map((product) => [
          product.name,
          product.orders.toString(),
          `${product.amount} FCFA`,
        ])

        doc.autoTable({
          startY: doc.autoTable.previous.finalY + 20,
          head: [["Produit", "Commandes", "Montant (FCFA)"]],
          body: productsData,
          theme: "grid",
          headStyles: { fillColor: [48, 86, 162] },
        })
      } else if (activeTab === "depenses") {
        // Ajouter les dépenses annuelles
        doc.setFontSize(14)
        doc.setTextColor(48, 86, 162)
        doc.text("Dépenses annuelles", 20, doc.autoTable.previous.finalY + 15)

        const expensesData = annualExpensesData.map((item) => [
          item.name,
          item[2023].toString(),
          item[2024].toString(),
          item[2024] > item[2023]
            ? `+${((item[2024] / item[2023] - 1) * 100).toFixed(1)}%`
            : `-${((1 - item[2024] / item[2023]) * 100).toFixed(1)}%`,
        ])

        doc.autoTable({
          startY: doc.autoTable.previous.finalY + 20,
          head: [["Mois", "2023 (FCFA)", "2024 (FCFA)", "Évolution"]],
          body: expensesData,
          theme: "grid",
          headStyles: { fillColor: [48, 86, 162] },
        })
      }

      // Ajouter un pied de page
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(100, 100, 100)
        doc.text("© 2024 SN PROVECI - Tous droits réservés", 20, 285)
        doc.text(`Page ${i} sur ${pageCount}`, 180, 285)
      }

      // Sauvegarder le PDF
      doc.save("rapport-performance.pdf")
    } catch (error) {
      console.error("Erreur lors de l'exportation du PDF:", error)
      alert("Une erreur est survenue lors de l'exportation du PDF. Veuillez réessayer.")
    } finally {
      setIsExporting(false)
    }
  }

 
  const handleNextPage = () => {
    if (indexOfLastItem < products.length) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handlePreviousPage = () => {
    if (indexOfFirstItem > 0) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  const fetchProductsOnOrder = (params, url) => {
    setIsLoadingProduct(true)
    crudData(params, url)
      .then((response) => {
        setIsLoadingProduct(false)
        if (response && response.status === 200) {
          const data = response.data.data || []
          setProducts(data)
        } else {
          console.error("Erreur HTTP:", response)
        }
      })
      .catch((error) => {
        setIsLoadingProduct(false)
        console.error("Erreur lors de la récupération des données:", error)
      })
  }

  

  const handleSetCurrentOrderID = (PcvID) => {
    if (PcvID === currentOrderID) return
    setCurrentOrderID(PcvID)
  }

  /**
   * Récupère la liste des commandes du client
   * @param {Object} params - Paramètres de filtrage et pagination
   * @param {string} url - URL de l'API
   */
  const fetchOrders = (params, url) => {
    setIsLoading(true)
    crudData(params, url)
      .then((response) => {
        setIsLoading(false)
        if (response && response.status === 200) {
          const data = response.data.data || []
          setTotalPage(() => {
            return Math.ceil(response.data["total"] / limit)
          })
          setOrders(data)
          if (data.length > 0) {
            setCurrentOrderID(data[0].PcvID)
          } else {
            setCurrentOrderID(null)
          }
        } else {
          console.error("Erreur HTTP:", response)
        }
      })
      .catch((error) => {
        setIsLoading(false)
        console.error("Erreur lors de la récupération des données:", error)
      })
  }

  

  const handleDownloadPDF = async () => {
    const blob = await pdf(
      <InvoicePDF products={products} order={orders.find((order) => order.PcvID === currentOrderID)} />,
    ).toBlob()
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `commande-#${currentOrderID}.pdf`
    link.click()
  }

  

  const goToPage = (page) => {
    setPage(page)
  }

 

  // Utiliser les données de l'image pour le graphique
const getMonthName = (monthNumber) => {
  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ]
  return monthNames[monthNumber - 1] || ""
}

  // const handleChangeYear = (selectedYear) => {
  //   setYear(selectedYear)
  // }

  // Animation pour le survol du graphique
  // const handleChartMouseEnter = () => {
  //   setChartHovered(true)
  // }

  // const handleChartMouseLeave = () => {
  //   setChartHovered(false)
  // }

  // Personnalisation du tooltip pour le graphique
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`${label}`}</p>
          <p className="tooltip-value">
            <span className="tooltip-dot" style={{ backgroundColor: "#3056a2" }}></span>
            {`Revenu: ${formatPrice(payload[0].value)} FCFA`}
          </p>
          <p className="tooltip-value">
            <span className="tooltip-dot" style={{ backgroundColor: "#878a99" }}></span>
            {`Nombre de commandes: ${payload[1] ? payload[1].value : 0}`}
          </p>
        </div>
      )
    }
    return null
  }

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!Array.isArray(statsData) || statsData.length === 0) {
      return []
    }

    return statsData.map((stat) => ({
      name: getMonthName(stat.MONTH),
      revenues: Number(stat.TOTAL_AMOUNT),
      commandes: Number(stat.NB_ORDER),
    }))
  }

  // Fonctions pour les factures
  

  const handleInvoiceActiveFilters = useCallback(
    (filterName, filterValue) => {
      setInvoiceActiveFilters((prevActiveFilters) => {
        const updatedFilters = structuredClone(prevActiveFilters)
        updatedFilters[filterName] = filterValue
        const params = new URLSearchParams()
        Object.keys(updatedFilters).forEach((key) => {
          updatedFilters[key] !== "" ? params.set(key, updatedFilters[key]) : params.delete(key)
        })
        params.set("limit", invoiceLimit)
        params.set("page", 1)
        setSearchParams(params)

        return updatedFilters
      })
    },
    [setSearchParams, invoiceLimit],
  )

  const handleClearInvoiceActiveFilters = () => {
    setSearchParams({})
  }

  const goToInvoicePage = (page) => {
    const params = new URLSearchParams(searchParams)
    params.set("page", page)
    setSearchParams(params)
    setInvoicePage(page)
  }

  const handleSetInvoiceLimit = (limit) => {
    const params = new URLSearchParams(searchParams)
    params.set("limit", limit)
    params.set("page", 1)
    setSearchParams(params)
    setInvoicePage(1)
    setInvoiceLimit(limit)
  }

  const handleInvoiceSearch = (e) => {
    const params = new URLSearchParams(searchParams)
    params.set("page", 1)
    params.set("search", e)
    setSearchParams(params)
    setInvoicePage(1)
    setInvoiceActiveFilters({
      ...invoiceActiveFilters,
      search: e,
    })
  }

  /**
   * Récupère les factures du client avec filtres appliqués
   * @param {Object} activeFilters - Filtres actifs (année, statut, recherche)
   * @returns {Promise} - Données des factures
   */
  const fetchInvoices = async (activeFilters) => {
    // ← Ne pas charger les factures si on est sur l'onglet commandes
    if (activeTab === "commandes") return []

    const userData = JSON.parse(localStorage.getItem("userData"))
    const data = new URLSearchParams()

    if (activeTab === "depenses") {
        data.append("FILTER_OPTIONS[PcvGCliID]", userData.LG_CLIID)
        data.append("LIMIT", 1000)
        data.append("PAGE", 1)
        data.append("mode", "listInvoices")
    } else {
        for (const key in activeFilters) {
            if (key === "search" && activeFilters[key] !== "") {
                data.append("FILTER_OPTIONS[search]", activeFilters[key])
            }
            if (key === "year" && activeFilters[key] !== "") {
                data.append("FILTER_OPTIONS[PcvDate]", activeFilters[key])
            }
            if (key === "statut" && activeFilters[key] !== "") {
                data.append("FILTER_OPTIONS[PcvEtatFNuf]", activeFilters[key])
            }
        }
        data.append("FILTER_OPTIONS[PcvGCliID]", userData.LG_CLIID)
        data.append("LIMIT", invoiceLimit)
        data.append("PAGE", invoicePage)
        data.append("mode", "listInvoices")
    }

    try {
        const response = await crudData(data, "CommandeManager.php", false)
        setInvoiceTotalPage(Math.ceil(response.data["total"] / invoiceLimit))
        return response.data.data
    } catch (error) {
        console.error(error)
        return []
    }
  }
 
  // Fonction pour récupérer les données des factures par mois pour la comparaison annuelle
  const getInvoiceDataByMonth = () => {
    // Si nous n'avons pas de factures, utiliser les données simulées
    if (!invoices || invoices.length === 0) {
      return annualExpensesData.map((item) => ({
        name: item.name,
        previousYear: item[2023],
        currentYear: item[2024],
      }))
    }

    // Initialiser les données pour chaque mois
    const monthNames = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ]

    const monthlyData = monthNames.map((name, index) => ({
      name,
      month: index + 1,
      previousYear: 0,
      currentYear: 0,
    }))

    // Calculer les totaux mensuels à partir des factures
    invoices.forEach((invoice) => {
      const invoiceDate = new Date(invoice.PcvDate)
      const invoiceYear = invoiceDate.getFullYear()
      const invoiceMonth = invoiceDate.getMonth()
      const invoiceAmount = Number.parseInt(invoice.PcvMtTTC)

      if (invoiceYear === Number(year)) {
        monthlyData[invoiceMonth].currentYear += invoiceAmount
      } else if (invoiceYear === Number(year) - 1) {
        monthlyData[invoiceMonth].previousYear += invoiceAmount
      }
    })

    return monthlyData
  }

  const chartData = prepareChartData()
  const navigate = useNavigate()

  //************************************************ */
  
  // Structure du tableau de bord:
  // 1. En-tête avec titre et bouton d'exportation
  // 2. Contrôles de filtrage par date
  // 3. Statistiques générales (cartes)
  // 4. Onglets pour les différentes sections de données
  // 5. Contenu spécifique à chaque onglet
  return (
    <div className="performance-dashboard" ref={dashboardRef}>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Chargement du dashboard...</p>
        </div>
      )}

      {isExporting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Génération du PDF en cours...</p>
        </div>
      )}

      {/* En-tête */}
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1 className="dashboard-title">STATISTIQUES DES PERFORMANCES - CLIENT</h1>
            </div>
            <div className="export-button-container">
              <button className="export-button" onClick={exportToPDF} disabled={isExporting}>
                {isExporting ? "PRÉPARATION..." : "EXPORTER"}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="dashboard-main container">
        {/* Filtres et contrôles */}
        {/* <div className="dashboard-controls">
          <div className="date-controls">
            <div className="select-wrapper">
              <select
                value={dateRange}
                onChange={handleDateRangeChange}
                className="date-select"
                style={{ height: "35px" }}
              >
                <option value="3mois">3 derniers mois</option>
                <option value="6mois">6 derniers mois</option>
                <option value="1an">Année en cours</option>
                <option value="custom">Personnalisé</option>
              </select>
            </div>
            <button className="icon-button" onClick={handleCalendarToggle}>
              <Calendar className="icon" />
            </button>
            {showCalendar && (
              <div className="calendar-popup" ref={calendarRef}>
                <div className="calendar-header">
                  <h3>Sélectionner une période</h3>
                </div>
                <div className="calendar-body">
                  <div className="date-input-group">
                    <label>Date de début:</label>
                    <input
                      type="date"
                      value={startDate.toISOString().split("T")[0]}
                      onChange={(e) => setStartDate(new Date(e.target.value))}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>Date de fin:</label>
                    <input
                      type="date"
                      value={endDate.toISOString().split("T")[0]}
                      onChange={(e) => setEndDate(new Date(e.target.value))}
                    />
                  </div>
                  <button 
                     className="apply-button" 
                     onClick={() => {
                        setShowCalendar(false)
                        // ← Recharger avec les dates custom
                        const formatDate = (d) => d.toISOString().split("T")[0]
                        fetchUserSumAmountOrders(formatDate(startDate), formatDate(endDate))
                      }}
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="search-controls">{/* Contrôles de recherche *</div>
        </div> */}

        {/* Statistiques générales */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">{stat.icon}</div>
                <div>
                  <p className="stat-description" style={{
                    fontSize: "1.2rem",
                    color: "#6c7a89",
                    fontWeight:"700",
                  }}>{stat.title}</p>
                  <h2 
                      className="stat-value"
                      style={{
                        // Rouge seulement pour la card encours
                        color: stat.title === "Encours actuel" && stat.isOverLimit
                            ? "#dc3545"
                            : undefined
                    }}
                  >
                    {stat.title === "Encours actuel" && stat.isOverLimit && "- "}
                    {stat.value}
                    {/* Afficher le plafond initial seulement pour la card encours */}
                    {stat.title === "Encours actuel" && stat.plafond && (
                        <p style={{
                            fontSize: "1rem",
                            color: "#6c7a89",
                            marginTop: "4px",
                            fontWeight: "500",
                        }}>
                            {stat.plafond}
                        </p>
                    )}
                  </h2>
                  {/* Ajout de la barre de progression pour le Montant total des Commandes */}
                  {stat.title === "Montant total des Commandes" && stat.target && (
                    <div className="progress-container">
                      <div className="progress-bar-wrapper">
                        <div
                          className="progress-bar"
                          style={{
                            width: `${stat.progress}%`,
                            backgroundColor: stat.progressColor,
                          }}
                        >
                          <span className="progress-text">{stat.progress}%</span>
                        </div>
                      </div>
                      <div className="progress-info">
                        <span>Objectif: {formatPrice(stat.target)} FCFA</span>
                        <span>Reste: {formatPrice(Math.max(0, stat.target - sumAmountOrders))} FCFA</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="stat-content">
                <div className={`stat-change ${stat.changeType}`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Onglets pour les différentes sections */}
        <div className="tabs-container">
          {/* Modifier la partie des onglets pour ajouter "Mes Commandes" */}
          <div className="tabs-list">
            <button
              className={`tab-button ${activeTab === "commandes" ? "active" : ""}`}
              onClick={() => handleTabChange("commandes")}
            >
              Statistiques Commandes / factures
            </button>

            <button
              className={`tab-button ${activeTab === "depenses" ? "active" : ""}`}
              onClick={() => handleTabChange("depenses")}
            >
              Comparaison des Dépenses Annuelles
            </button>
            <button
              className={`tab-button ${activeTab === "produits" ? "active" : ""}`}
              onClick={() => handleTabChange("produits")}
            >
              Produits les plus consultés
            </button>
            <button
              className={`tab-button ${activeTab === "mescommandes" ? "active" : ""}`}
              onClick={() => handleTabChange("mescommandes")}
            >
              Historiques des Commandes
            </button>
            <button
              className={`tab-button ${activeTab === "factures" ? "active" : ""}`}
              onClick={() => handleTabChange("factures")}
            >
              Historiques des factures
            </button>
          </div>
          {/* ══ ONGLET : Statistiques Commandes / Factures ══ */}
          <div className={`tab-content ${activeTab === "commandes" ? "active" : ""}`}>
            <div
              className={`chart-card full-width ${animateCharts ? "animate" : ""}`}
              style={{ marginBottom: "20px" }}
              onMouseEnter={handleChartMouseEnter}
              onMouseLeave={handleChartMouseLeave}
              ref={chartRef}
            >

              <div className="chart-header">
                  <div className="chart-title-wrapper">
                    <p className="chart-description" style={{ fontSize: "1.5rem"}}>
                          Évolution des commandes par mois avec montants
                    </p>
                
                  
                  {/* ── Boutons filtres ── */}
                  {/* <div style={{ display: "flex", gap: "10px", alignItems: "center", marginLeft: "auto"}}>   */}

                    {/* 1er bouton : Type de Commande */}
                    <select
                      value={filterType} 
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        // if (handleChangeType) handleChangeType(e.target.value); // Si vous avez une fonction de callback dédiée
                      }}
                      style={{
                        padding: "10px 18px",
                        border: "none",
                        background: "#214293",
                        color: "white",
                        borderRadius: "20px",
                        cursor: "pointer",
                        fontFamily: "Poppins",
                        fontWeight: "600",
                        fontSize: "1.2rem",
                        marginLeft: "600px" 
                      }}
                    >
                      <option value="">Tous les types</option>
                      <option value="8Sens">8Sens</option>
                      <option value="extranet">Extranet</option>
                      {/* Ajoutez vos types de commandes ici */}
                    </select>                  


                    {/* Bouton Année — affiche l'année en cours par défaut */}
                    <select
                      value={filterYear}
                      onChange={(e) => {
                          setFilterYear(e.target.value)
                          handleChangeYear(e.target.value || new Date().getFullYear())
                      }}
                      style={{
                          padding: "10px 18px",
                          border: "none",
                          background: "#214293",
                          color: "white",
                          borderRadius: "20px",
                          cursor: "pointer",
                          fontFamily: "Poppins",
                          fontWeight: "600",
                          fontSize: "1.2rem",
                          marginLeft: "auto" 
                      }}>
                      {/* Valeur par défaut = année en cours */}
                      <option value="">{new Date().getFullYear()}</option>
                      {[2020, 2021, 2022, 2023, 2024, 2025].map(y => (
                          <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>    

                <div className="chart-content">
                  {/* ── Résumé chiffres ── */}
                  <div className="card-header p-0 border-0 bg-light-subtle">
                    <div className="row g-0 text-center">
                      <div className="col-6 col-sm-6">
                        <div className="p-3 border border-dashed border-start-0">
                          <h5 className="mb-1">
                            {filteredChartData.reduce((acc, val) => acc + Number(val.commandes), 0)}
                          </h5>
                          <p className="mb-0">
                            Total Commandes année
                    
                          </p>
                        </div>
                      </div>
                      <div className="col-6 col-sm-6">
                        <div className="p-3 border border-dashed border-start-0">
                          <h5 className="mb-1">
                              {formatPrice(filteredChartData.reduce((acc, val) => acc + Number(val.revenues), 0))} FCFA
                          </h5>
                          <p className="mb-0">Montant total</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Graphique ── */}
                  {isLoadingStats ? (
                    <div style={{ textAlign: "center", padding: "60px" }}>
                      <div className="spinner" style={{ margin: "0 auto" }}></div>
                      <p style={{ marginTop: "12px", color: "#6c7a89" }}>Chargement...</p>
                    </div>
                  ) : filteredChartData.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "60px", color: "#6c7a89" }}>
                        Aucune commande pour cette période
                      </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={450}>
                      <RechartsBarChart
                        data={filteredChartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        barSize={filteredChartData.length === 1 ? 80 : 35}
                        className="animated-chart"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                        {/* Axe X = Mois */}
                        <XAxis
                          dataKey="name"
                          tick={{ fill: "#6c7a89" }}
                          axisLine={{ stroke: "#e0e0e0" }}
                          tickLine={{ stroke: "#e0e0e0" }}
                        />

                        {/* Axe Y = Nombre de commandes */}
                        <YAxis
                          allowDecimals={false}
                          tick={{ fill: "#6c7a89" }}
                          axisLine={{ stroke: "#e0e0e0" }}
                          tickLine={{ stroke: "#e0e0e0" }}
                          domain={[0, "auto"]}
                          label={{
                          value: "Nombre de commandes",
                          angle: -90,
                          position: "insideLeft",
                          fill: "#6c7a89",
                          style: { textAnchor: "middle" },
                          }}
                        />

                        {/* Tooltip : affiche le montant au survol */}
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null
                            return (
                              <div style={{
                                background: "white",
                                padding: "14px",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                border: "1px solid rgba(48,86,162,0.1)",
                              }}>
                                <p style={{ fontWeight: "700", color: "#214293", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px"}}>
                                    <Calendar size={16} color="#214293" /> 
                                    {label}
                                </p>
                                <p style={{ color: "#214293", margin: "4px 0", display: "flex", alignItems: "center", gap: "6px" }}>
                                  <Package size={16} color="#214293" />
                                   Commandes : <strong>{payload[0]?.value}</strong>
                                </p>
                                <p style={{ color: "#27ae60", margin: "4px 0", display: "flex", alignItems: "center", gap: "6px"  }}>
                                  <Coins size={16} color="#27ae60" />
                                   Montant : <strong>
                                      {formatPrice(payload[0]?.payload?.revenues || 0)} FCFA
                                  </strong>
                                </p>
                              </div>
                            )
                          }}
                        />

                        {/* Légende dynamique dans le graphique */}
                        <Legend
                          payload={[
                            {
                              value: `Commandes — ${filterYear || new Date().getFullYear()}`,
                              type: "rect",
                              color: "#3056a2",
                            },
                          ]}
                          wrapperStyle={{ paddingTop: "20px" }}
                        />

                        {/* Barres = nombre de commandes */}
                        <Bar
                          dataKey="commandes"
                          fill="#3056a2"
                          stroke="#214293"
                          strokeWidth={1}
                          radius={[6, 6, 0, 0]}
                          animationDuration={1500}
                          animationEasing="ease-in-out"
                          isAnimationActive={true}
                        />
                        </RechartsBarChart>
                      </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* section "Mes Commandes" */}
          <div className={`tab-content ${activeTab === "mescommandes" ? "active" : ""}`}>
            {/* Mes commandes */}
            <div
              className={`chart-card full-width ${animateCharts ? "animate" : ""}`}
              style={{ animationDelay: "0.2s" }}
            >
              <div className="chart-header">
                <div className="chart-title-wrapper">
                  <h3 className="chart-title">Mes commandes</h3>
                  <button onClick={() => setShowModal(true)} type="button" className="export-action-button">
                    Exporter
                  </button>
                </div>
                <div className="chart-filters">
                  <label htmlFor="year-filter">Afficher commandes de: </label>
                  {/* <select
                    name="year-filter"
                    id="year-filter"
                    className="form-select"
                    style={{
                      padding: "6px 10px",
                      fontFamily: "Poppins",
                      marginLeft: "10px",
                      border: "1px solid rgba(48, 86, 162, 0.2)",
                      borderRadius: "4px",
                      background: "#fff",
                      color: "#3056a2",
                    }}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    <option value="all">Toutes</option>
                    <option value="2020">2020</option>
                    <option value="2021">2021</option>
                    <option value="2022">2022</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                  </select> */}
                  <select
                      value={orderYear}
                      onChange={(e) => setOrderYear(e.target.value)}
                      style={{
                          padding: "6px 10px", fontFamily: "Poppins",
                          border: "1px solid rgba(48,86,162,0.2)",
                          borderRadius: "4px", background: "#fff", color: "#3056a2",
                      }}
                  >
                      {/* Toutes les années = pas de filtre */}
                      <option value="all">Toutes les années</option>
                      {[2020, 2021, 2022, 2023, 2024, 2025].map(y => (
                          <option key={y} value={y}>{y}</option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="chart-content">
                <div className="orders-container">
                  <div className="orders-list">
                    <table className="data-table orders-table">
                      <thead>
                        <tr>
                          <th>N° Commande</th>
                          <th>Date</th>
                          <th>Montant TTC</th>
                          <th>Montant HT</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders && orders.length > 0 ? (
                          orders.map((order, index) => (
                            <tr
                              key={index}
                              className={`order-row ${currentOrderID === order.PcvID ? "active" : ""}`}
                              onClick={() => handleSetCurrentOrderID(order.PcvID)}
                            >
                              <td>{order.PcvID}</td>
                              <td>{formatDateOriginal(order.PcvDate)}</td>
                              <td>{formatPrice(Number.parseInt(order.PcvMtHT + order.PcvMtHT * 0.05))} FCFA</td>
                              <td>{formatPrice(Number.parseInt(order.PcvMtHT))} FCFA</td>
                              <td>
                                <span className="status-badge success">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width={16}
                                    height={16}
                                  >
                                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM17.4571 9.45711L11 15.9142L6.79289 11.7071L8.20711 10.2929L11 13.0858L16.0429 8.04289L17.4571 9.45711Z"></path>
                                  </svg>
                                  Validée
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : isLoading ? (
                          <tr>
                            <td colSpan={5} className="loading-cell">
                              Chargement des données...
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan={5} className="empty-cell">
                              Aucune donnée disponible
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className="pagination-container">
                      <div className="pagination">
                        {pageNumbers.map((pageNum, index) => (
                          <button
                            key={index}
                            className={`page-item ${pageNum === page ? "active" : ""}`}
                            onClick={() => goToPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Dans la partie order-details, remplacer tout le contenu actuel par cette version améliorée: */}
                  <div className="order-details">
                    <div className="order-summary-header">
                      <h3>Résumé de la commande</h3>
                      <div className="order-summary-id">{currentOrderID && <span>#{currentOrderID}</span>}</div>
                    </div>

                    {products && products.length > 0 ? (
                      <>
                        <div className="order-products-header">
                          <div className="product-col-name">Produit</div>
                          <div className="product-col-qty">Qté</div>
                          <div className="product-col-price">Prix unitaire</div>
                          <div className="product-col-total">Total</div>
                        </div>

                        <div className="order-products-list">
                          {currentProducts.map((product, index) => (
                            <div key={index} className="order-product-item">
                              <div className="product-col-name">{product.ArtLib}</div>
                              <div className="product-col-qty">{product.int_cprquantity}</div>
                              <div className="product-col-price">
                                {formatPrice(Number.parseInt(product.ArtPrixBase))} FCFA
                              </div>
                              <div className="product-col-total">
                                {formatPrice(product.ArtPrixBase * product.int_cprquantity)} FCFA
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="order-summary-totals">
                          <div className="summary-row">
                            <span>Sous-total</span>
                            <span>
                              {formatPrice(
                                currentProducts.reduce((acc, item) => acc + item.ArtPrixBase * item.int_cprquantity, 0),
                              )}{" "}
                              FCFA
                            </span>
                          </div>
                          <div className="summary-row">
                            <span>TVA (5%)</span>
                            <span>
                              {formatPrice(
                                currentProducts.reduce(
                                  (acc, item) => acc + item.ArtPrixBase * item.int_cprquantity * 0.05,
                                  0,
                                ),
                              )}{" "}
                              FCFA
                            </span>
                          </div>
                          <div className="summary-row total">
                            <span>Total TTC</span>
                            <span>
                              {formatPrice(
                                currentProducts.reduce(
                                  (acc, item) => acc + item.ArtPrixBase * item.int_cprquantity * 1.05,
                                  0,
                                ),
                              )}{" "}
                              FCFA
                            </span>
                          </div>
                        </div>

                        <div className="order-pagination">
                          <button
                            className={`pagination-btn ${indexOfFirstItem <= 0 ? "disabled" : ""}`}
                            onClick={handlePreviousPage}
                            disabled={indexOfFirstItem <= 0}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M15 18l-6-6 6-6" />
                            </svg>
                            Précédent
                          </button>

                          <div className="pagination-info">
                            Page {currentPage} sur {Math.ceil(products.length / itemPerPage)}
                          </div>

                          <button
                            className={`pagination-btn ${indexOfLastItem >= products.length ? "disabled" : ""}`}
                            onClick={handleNextPage}
                            disabled={indexOfLastItem >= products.length}
                          >
                            Suivant
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="order-empty-state">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#3056a2"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                          <line x1="12" y1="22.08" x2="12" y2="12"></line>
                        </svg>
                        <p>Sélectionnez une commande pour voir les détails</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal d'exportation */}
            <Modal size="lg" show={showModal} onHide={() => setShowModal(false)} centered className="export-modal">
              <Modal.Header className="border-0 pb-0">
                <Modal.Title className="w-100">
                  <h3 className="modal-title text-primary">Exporter details commande N° #{currentOrderID}</h3>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="text-center pt-4">
                <p className="mb-4">Choisissez le format de l'export des details</p>
                <div className="export-options-container">
                  <div className="export-options">
                    <div className="export-option" onClick={handleDownloadPDF}>
                      <div className="export-icon pdf-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="48" height="48">
                          <path
                            fill="#ff0000"
                            d="M181.9 256.1c-5-16-4.9-46.9-2-46.9 8.4 0 7.6 36.9 2 46.9zm-1.7 47.2c-7.7 20.2-17.3 43.3-28.4 62.7 18.3-7 39-17.2 62.9-21.9-12.7-9.6-24.9-23.4-34.5-40.8zM86.1 428.1c0 .8 13.2-5.4 34.9-40.2-6.7 6.3-29.1 24.5-34.9 40.2zM248 160h136v328c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V24C0 10.7 10.7 0 24 0h200v136c0 13.2 10.8 24 24 24zm-8 171.8c-20-12.2-33.3-29-42.7-53.8 4.5-18.5 11.6-46.6 6.2-64.2-4.7-29.4-42.4-26.5-47.8-6.8-5 18.3-.4 44.1 8.1 77-11.6 27.6-28.7 64.6-40.8 85.8-.1 0-.1.1-.2.1-27.1 13.9-73.6 44.5-54.5 68 5.6 6.9 16 10 21.5 10 17.9 0 35.7-18 61.1-61.8 25.8-8.5 54.1-19.1 79-23.2 21.7 11.8 47.1 19.5 64 19.5 29.2 0 31.2-32 19.7-43.4-13.9-13.6-54.3-9.7-73.6-7.2zM377 105L279 7c-4.5-4.5-10.6-7-17-7h-6v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-74.1 255.3c4.1-2.7-2.5-11.9-42.8-9 37.1 15.8 42.8 9 42.8 9z"
                          />
                        </svg>
                      </div>
                      <div className="export-label">PDF</div>
                    </div>

                    <div className="export-option">
                      <OrderForm
                        orderDetails={orders.find((order) => order.PcvID === currentOrderID)}
                        orderItems={products}
                        className="export-excel-wrapper"
                      >
                        <div className="export-icon excel-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="48" height="48">
                            <path
                              fill="#008000"
                              d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm60.1 106.5L224 336l60.1 93.5c5.1 8-.6 18.5-10.1 18.5h-34.9c-4.4 0-8.5-2.4-10.6-6.3C208.9 405.5 192 373 192 373c-6.4 14.8-1
                               0 20-36.6 68.8-2.1 3.9-6.1 6.3-10.5 6.3H110c-9.5 0-15.2-10.5-10.1-18.5l60.3-93.5-60.3-93.5c-5.2-8 .6-18.5 10.1-18.5h34.8c4.4 0 8.5 2.4 10.6 6.3 26.1 48.8 20 33.6 36.6 68.5 0 0 6.1-11.7 36.6-68.5 2.1-3.9 6.2-6.3 10.6-6.3H274c9.5-.1 15.2 10.4 10.1 18.4zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"
                            />
                          </svg>
                        </div>
                        <div className="export-label">Excel</div>
                      </OrderForm>
                    </div>

                    <CSVLink
                      data={products.map((product) => ({
                        Code: product.ArtCode,
                        Libellé: product.ArtLib,
                        Prix: product.ArtPrixBase,
                        Quantité: product.int_cprquantity,
                        Total: product.int_cprquantity * product.ArtPrixBase,
                      }))}
                      separator=";"
                      filename={`commande-#${currentOrderID}.csv`}
                      target="_blank"
                      enclosingCharacter=""
                      className="export-option"
                    >
                      <div className="export-icon csv-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="48" height="48">
                          <path
                            fill="#008000"
                            d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm-96 144c0 4.4-3.6 8-8 8h-8c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h8c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8h-8c-26.5 0-48-21.5-48-48v-32c0-26.5 21.5-48 48-48h8c4.4 0 8 3.6 8 8v16zm96 0c0 4.4-3.6 8-8 8h-40v24h24c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8h-24v32h40c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8h-64c-4.4 0-8-3.6-8-8V152c0-4.4 3.6-8 8-8h64c4.4 0 8 3.6 8 8v16zm65.8 78.2c-4.4 0-8.1-3.5-8.1-7.8V152c0-4.4 3.6-8 8-8h16c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8h-8v32h8c4.4 0 8 3.6 8 8v16c0 4.4-3.6 8-8 8h-8v20c0 4.4-3.6 8-8 8h-7.9zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"
                          />
                        </svg>
                      </div>
                      <div className="export-label">CSV</div>
                    </CSVLink>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className="border-0">
                <Button variant="warning" onClick={() => setShowModal(false)} className="close-button">
                  FERMER
                </Button>
              </Modal.Footer>
            </Modal>
          </div>

          {/* Contenu de l'onglet Dépenses */}
          <div className={`tab-content ${activeTab === "depenses" ? "active" : ""}`}>
            <div className={`chart-card full-width ${animateCharts ? "animate" : ""}`}>

              <div className="chart-header">
                <div className="chart-title-wrapper">
                    {/* <h3 className="chart-title">Comparaison des Dépenses Annuelles</h3> */}
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", marginLeft: "auto"}}>
                        <span style={{ color: "#6c7a89", fontSize: "0.9rem" }}>De</span>
                        <select
                            value={compareYearStart}
                            onChange={(e) => setCompareYearStart(Number(e.target.value))}
                            style={{
                                padding: "6px 12px",
                                border: "none",
                                background: "#214293",
                                color: "white",
                                borderRadius: "20px",
                                cursor: "pointer",
                                fontFamily: "Poppins",
                                fontWeight: "600",
                            }}
                        >
                            {[2020,2021,2022,2023,2024,2025].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <span style={{ color: "#6c7a89", fontSize: "0.9rem" }}>à</span>
                        <select
                            value={compareYearEnd}
                            onChange={(e) => setCompareYearEnd(Number(e.target.value))}
                            style={{
                                padding: "6px 12px",
                                border: "none",
                                background: "#5b7bc0",
                                color: "white",
                                borderRadius: "20px",
                                cursor: "pointer",
                                fontFamily: "Poppins",
                                fontWeight: "600",
                            }}
                        >
                            {[2020,2021,2022,2023,2024,2025,2026].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Légende dynamique */}
                <div style={{ display: "flex", gap: "20px", fontSize: "1.2rem" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ width: "14px", height: "14px", borderRadius: "3px", background: "#3056a2", display: "inline-block" }}></span>
                      <strong style={{ color: "#3056a2" }}>{compareYearStart}</strong>
                      — Total : {formatPrice(compareData.reduce((acc, d) => acc + (d.startYear || 0), 0))} FCFA
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ width: "14px", height: "14px", borderRadius: "3px", background: "#5b7bc0", display: "inline-block" }}></span>
                      <strong style={{ color: "#5b7bc0" }}>{compareYearEnd}</strong>
                      — Total : {formatPrice(compareData.reduce((acc, d) => acc + (d.endYear || 0), 0))} FCFA
                  </span>
                </div>
              </div>

              <div className="chart-content">
                {isLoadingCompare ? (
                    <div style={{ textAlign: "center", padding: "60px" }}>
                        <div className="spinner" style={{ margin: "0 auto" }}></div>
                        <p style={{ marginTop: "12px", color: "#6c7a89" }}>Chargement...</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={420}>
                        <RechartsBarChart
                            data={compareData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                            barSize={40}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                                dataKey="name"
                                tick={{ fill: "#6c7a89" }}
                                axisLine={{ stroke: "#e0e0e0" }}
                                tickLine={{ stroke: "#e0e0e0" }}
                            />
                            <YAxis
                                tick={{ fill: "#6c7a89" }}
                                axisLine={{ stroke: "#e0e0e0" }}
                                tickLine={{ stroke: "#e0e0e0" }}
                                tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`}
                                label={{
                                    value: "Montant (FCFA)",
                                    angle: -90,
                                    position: "insideLeft",
                                    fill: "#6c7a89",
                                    style: { textAnchor: "middle" },
                                }}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null
                                    const startVal = payload.find(p => p.dataKey === "startYear")?.value || 0
                                    const endVal   = payload.find(p => p.dataKey === "endYear")?.value || 0
                                    const total    = startVal + endVal
                                    const diff = startVal > 0
                                        ? (((endVal - startVal) / startVal) * 100).toFixed(1)
                                        : null
                                    return (
                                        <div style={{
                                            background: "white", padding: "14px",
                                            borderRadius: "8px",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                            border: "1px solid rgba(48,86,162,0.1)"
                                        }}>
                                            <p style={{ fontWeight: "700", color: "#214293", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
                                              <Calendar size={16} color="#214293" />
                                                  {label}
                                            </p>
                                            <p style={{ color: "#3056a2", margin: "4px 0" }}>
                                                {compareYearStart} : <strong>{formatPrice(startVal)} FCFA</strong>
                                            </p>
                                            <p style={{ color: "#5b7bc0", margin: "4px 0" }}>
                                                {compareYearEnd} : <strong>{formatPrice(endVal)} FCFA</strong>
                                            </p>
                                            <p style={{
                                                margin: "8px 0 4px", paddingTop: "8px",
                                                borderTop: "1px solid #eee",
                                                color: "#214293", fontWeight: "600"
                                            }}>
                                                Total : {formatPrice(total)} FCFA
                                            </p>
                                            {diff !== null && (
                                                <p style={{
                                                    color: endVal >= startVal ? "#27ae60" : "#e74c3c",
                                                    fontWeight: "600", margin: "4px 0",
                                                    display: "flex", alignItems: "center", gap: "6px"
                                                }}>
                                                    {endVal >= startVal 
                                                        ? <TrendingUp size={16} color="#27ae60" />
                                                        : <TrendingDown size={16} color="#e74c3c" />
                                                      } 
                                                      Évolution : {diff}%
                                                </p>
                                            )}
                                        </div>
                                    )
                                }}
                            />
                            <Legend
                                payload={[
                                    { value: String(compareYearStart), type: "rect", color: "#3056a2" },
                                    { value: String(compareYearEnd),   type: "rect", color: "#5b7bc0" },
                                ]}
                                wrapperStyle={{ paddingTop: "16px" }}
                            />

                            {/* ← stackId="stack" = barres empilées */}
                            <Bar
                                dataKey="startYear"
                                name={String(compareYearStart)}
                                stackId="stack"
                                fill="#3056a2"
                                radius={[0, 0, 0, 0]}
                                isAnimationActive
                                animationDuration={1000}
                            />
                            <Bar
                                dataKey="endYear"
                                name={String(compareYearEnd)}
                                stackId="stack"
                                fill="#5b7bc0"
                                radius={[4, 4, 0, 0]}
                                isAnimationActive
                                animationDuration={1200}
                            />
                        </RechartsBarChart>
                    </ResponsiveContainer>
                )}
            </div>
            </div>
          </div>

          {/* Contenu de l'onglet Produits */}
          <div className={`tab-content ${activeTab === "produits" ? "active" : ""}`}>
            <div className="chart-grid">
              <div
                className={`chart-card full-width ${animateCharts ? "animate" : ""}`}
                style={{ animationDelay: "0.2s" }}
              >
                {/* Intégration du composant MostPopularProductsCard */}
                <div
                  className="d-flex justify-content-between"
                  style={{
                    padding: "0.75rem 1.5rem",
                    borderBottom: "1px solid rgba(48, 86, 162, 0.05)",
                  }}
                >
                  <h4 className="mb-0 mr-4" style={{ color: "#3056a2", fontWeight: "700" }}>
                    Produits les plus consultés
                  </h4>
                  <select
                    name="month"
                    id="month"
                    className="form-select"
                    style={{
                      padding: "6px 10px",
                      fontFamily: "Poppins",
                      marginRight: "10px",
                      border: "none",
                      background: "#214293",
                      color: "white",
                      borderRadius: "4px",
                      transition: "all 0.3s ease",
                    }}
                    onChange={(e) => handleChangeMonth(e.target.value)}
                  >
                    <option value="all">Tous les mois</option>
                    <option value="01">Janvier</option>
                    <option value="02">Février</option>
                    <option value="03">Mars</option>
                    <option value="04">Avril</option>
                    <option value="05">Mai</option>
                    <option value="06">Juin</option>
                    <option value="07">Juillet</option>
                    <option value="08">Août</option>
                    <option value="09">Septembre</option>
                    <option value="10">Octobre</option>
                    <option value="11">Novembre</option>
                    <option value="12">Décembre</option>
                  </select>
                </div>
                <div className="chart-content">
                  <table className="cart-table data-table">
                    <thead>
                      <tr>
                        <th
                          className="product-quantity"
                          style={{
                            textAlign: "left",
                            padding: "0.75rem 1.5rem",
                            margin: 0,
                            background: "rgba(48, 86, 162, 0.05)",
                            color: "#3056a2",
                            fontWeight: "600",
                          }}
                        >
                          <span>Nom du produit</span>
                        </th>
                        <th
                          className="product-price"
                          style={{
                            textAlign: "left",
                            padding: "0.75rem 1.5rem",
                            margin: 0,
                            background: "rgba(48, 86, 162, 0.05)",
                            color: "#3056a2",
                            fontWeight: "600",
                          }}
                        >
                          <span>Prix</span>
                        </th>
                        <th
                          className="product-quantity"
                          style={{
                            textAlign: "left",
                            padding: "0.75rem 1.5rem",
                            margin: 0,
                            background: "rgba(48, 86, 162, 0.05)",
                            color: "#3056a2",
                            fontWeight: "600",
                          }}
                          
                        >
                          {/* <span>Stock</span> */}
                        </th>
                        <th
                          className="product-subtotal"
                          style={{
                            textAlign: "left",
                            padding: "0.75rem 1.5rem",
                            margin: 0,
                            background: "rgba(48, 86, 162, 0.05)",
                            color: "#3056a2",
                            fontWeight: "600",
                          }}
                        >
                          <span>Vues</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingProducts ? (
                        <tr>
                          <td colSpan={4} className="text-center py-4">
                            <div className="spinner" style={{ margin: "0 auto" }}></div>
                            <p className="mt-2">Chargement des produits...</p>
                          </td>
                        </tr>
                      ) : productsStats?.data?.length > 0 ? (
                        productsStats.data.map((product, index) => (
                          <tr
                            className="item-hover"
                            style={{
                              background: "none",
                              color: "rgb(54, 86, 161)",
                              transition: "all 0.3s ease",
                            }}
                            key={index}
                            onClick={() => navigate(`/${product.PROSLUG}`)}
                          >
                            <td className="product-thumbnail">
                              <p
                                style={{
                                  padding: "0.75rem 1.5rem",
                                  margin: 0,
                                  fontWeight: "500",
                                }}
                              >
                                {product.PRODESCRIPTION}
                              </p>
                            </td>
                            <td className="product-price">
                              <p
                                style={{
                                  padding: "0.75rem 1.5rem",
                                  margin: 0,
                                }}
                              >
                                {formatPrice(Number.parseInt(product.PROPRICE)) + " FCFA"}
                              </p>
                            </td>
                            <td className="product-name">
                              <p
                                className=""
                                style={{
                                  padding: "0.75rem 1.5rem",
                                  margin: 0,
                                }}
                              >
                                <ShoppingCart size={20} />
                              </p>
                            </td>
                            <td className="product-price">
                              <p
                                style={{
                                  padding: "0.75rem 1.5rem",
                                  margin: 0,
                                  fontWeight: "bold",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  style={{ color: "#3056a2" }}
                                >
                                  <path
                                    fill="currentColor"
                                    d="m23.5 17l-5 5l-3.5-3.5l1.5-1.5l2 2l3.5-3.5zM12 9a3 3 0 0 1 3 3a3 3 0 0 1-3 3a3 3 0 0 1-3-3a3 3 0 0 1 3-3m0-4.5c5 0 9.27 3.11 11 7.5c-.25.65-.56 1.26-.92 1.85a5.8 5.8 0 0 0-1.9-.73l.64-1.12a9.821 9.821 0 0 0-17.64 0A9.82 9.82 0 0 0 12 17.5l1.21-.07c-.14.5-.21 1.03-.21 1.57v.46l-1 .04c-5 0-9.27-3.11-11-7.5c1.73-4.39 6-7.5 11-7.5"
                                  />
                                </svg>
                                {product.PROVIEWED}
                              </p>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center py-4">
                            <p>Aucun produit trouvé</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-between align-items-center flex-wrap justify-content-sm-center mt-4">
                    <div className="pagination-container">
                      <div className="pagination">
                        {pageNumbers.map((pageNum, index) => (
                          <button
                            key={index}
                            className={`page-item ${pageNum === productPage ? "active" : ""}`}
                            onClick={() => goToProductPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenu de l'onglet Historiques des factures */}
          <div className={`tab-content ${activeTab === "factures" ? "active" : ""}`}>
            <div
              className={`chart-card full-width ${animateCharts ? "animate" : ""}`}
              style={{ animationDelay: "0.2s" }}
            >
              <div className="chart-header">
                <div className="chart-title-wrapper">
                  <h3 className="chart-title">Mes factures</h3>
                </div>
              </div>
              <div className="chart-content">
                <div className="d-flex align-items-top mb-4 flex-wrap">
                  <div className="search-bar mb-4" style={{ marginRight: "20px" }}>
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        color: "#336699",
                        borderColor: "#336699",
                        borderWidth: "2px",
                        padding: "8px 12px",
                        borderRadius: "4px",
                      }}
                      placeholder="N° Commande"
                      value={invoiceActiveFilters.search}
                      onChange={(e) => handleInvoiceSearch(e.target.value)}
                    />
                  </div>
                  <div>
                    <h4 style={{ margin: "0px 20px 10px 0px", color: "#3056a2" }}>Filtrer par:</h4>
                    <div className="d-flex align-items-center gap-4">
                      <div>
                        <label htmlFor="invoice-year" style={{ marginRight: "8px" }}>
                          Année:{" "}
                        </label>
                        <select
                          name="invoice-year"
                          id="invoice-year"
                          className="form-select"
                          style={{
                            padding: "6px 10px",
                            fontFamily: "Poppins",
                            marginRight: "10px",
                            border: "none",
                            background: "#214293",
                            color: "white",
                            borderRadius: "4px",
                          }}
                          value={invoiceActiveFilters.year}
                          onChange={(e) => handleInvoiceActiveFilters("year", e.target.value)}
                        >
                          <option value="">Toutes</option>
                          <option value="2020">2020</option>
                          <option value="2021">2021</option>
                          <option value="2022">2022</option>
                          <option value="2023">2023</option>
                          <option value="2024">2024</option>
                          <option value="2025">2025</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="invoice-statut" style={{ marginRight: "8px" }}>
                          Statut:{" "}
                        </label>
                        <select
                          name="invoice-statut"
                          id="invoice-statut"
                          className="form-select"
                          style={{
                            padding: "6px 10px",
                            fontFamily: "Poppins",
                            marginRight: "10px",
                            border: "none",
                            background: "#214293",
                            color: "white",
                            borderRadius: "4px",
                          }}
                          value={invoiceActiveFilters.statut}
                          onChange={(e) => handleInvoiceActiveFilters("statut", e.target.value)}
                        >
                          <option value="">Toutes</option>
                          <option value="NufPcvEtatFNo">Non réglé</option>
                          <option value="NufPCVEtatFT">Réglé</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="col-lg-12 pr-lg-4 mb-6 table-min-h d-flex flex-column"
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    whiteSpace: "nowrap",
                    position: "relative",
                  }}
                >
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", minWidth: "150px" }}>
                          <span>N° Commande</span>
                        </th>
                        <th style={{ textAlign: "left", minWidth: "150px" }}>
                          <span>Date</span>
                        </th>
                        <th style={{ textAlign: "left", minWidth: "150px" }}>
                          <span>Montant TTC</span>
                        </th>
                        <th style={{ textAlign: "left", minWidth: "150px" }}>
                          <span>Montant HT</span>
                        </th>
                        <th style={{ textAlign: "center" }}>
                          <span>Statut</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingInvoices ? (
                        <tr>
                          <td colSpan={5} className="text-center" style={{ padding: "30px" }}>
                            <div className="spinner" style={{ margin: "0 auto" }}></div>
                            <p className="mt-2">Chargement des données...</p>
                          </td>
                        </tr>
                      ) : invoices && invoices.length > 0 ? (
                        invoices.map((invoice, index) => (
                          <tr key={index} className="item-hover">
                            <td>
                              <p style={{ margin: "1em 0px" }}>#{invoice.PcvID}</p>
                            </td>
                            <td>
                              <p style={{ margin: "1em 0px" }}>{formatDateOriginal(invoice.PcvDate)}</p>
                            </td>
                            <td>
                              <p style={{ margin: "1em 0px" }}>{formatPrice(Number.parseInt(invoice.PcvMtTTC))} FCFA</p>
                            </td>
                            <td>
                              <p style={{ margin: "1em 0px" }}>{formatPrice(Number.parseInt(invoice.PcvMtHT))} FCFA</p>
                            </td>
                            <td>
                              <p className="text-center" style={{ margin: "1em 0px" }}>
                                {invoice.PcvEtatFNuf === "NufPCVEtatFNo" || invoice.PcvEtatFNuf === "NufPcvEtatFNo" ? (
                                  <span
                                    className="status-badge warning"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "0.4rem 0.8rem",
                                      borderRadius: "20px",
                                      fontSize: "0.85rem",
                                      fontWeight: "600",
                                      backgroundColor: "rgba(253, 126, 20, 0.1)",
                                      color: "#fd7e14",
                                      border: "1px solid rgba(253, 126, 20, 0.2)",
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      width={16}
                                      height={16}
                                      style={{ marginRight: "5px" }}
                                    >
                                      <path d="M6 4H4V2H20V4H18V6C18 7.61543 17.1838 8.91468 16.1561 9.97667C15.4532 10.703 14.598 11.372 13.7309 12C14.598 12.628 15.4532 13.297 16.1561 14.0233C17.1838 15.0853 18 16.3846 18 18V20H20V22H4V20H6V18C6 16.3846 6.81616 15.0853 7.8439 14.0233C8.54682 13.297 9.40202 12.628 10.2691 12C9.40202 11.372 8.54682 10.703 7.8439 9.97667C6.81616 8.91468 6 7.61543 6 6V4ZM8 4V6C8 6.68514 8.26026 7.33499 8.77131 8H15.2287C15.7397 7.33499 16 6.68514 16 6V4H8ZM12 13.2219C10.9548 13.9602 10.008 14.663 9.2811 15.4142C9.09008 15.6116 8.92007 15.8064 8.77131 16H15.2287C15.0799 15.8064 14.9099 15.6116 14.7189 15.4142C13.992 14.663 13.0452 13.9602 12 13.2219Z"></path>
                                    </svg>
                                    Non réglé
                                  </span>
                                ) : (
                                  <span
                                    className="status-badge success"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "0.4rem 0.8rem",
                                      borderRadius: "20px",
                                      fontSize: "0.85rem",
                                      fontWeight: "600",
                                      backgroundColor: "rgba(39, 174, 96, 0.1)",
                                      color: "#27ae60",
                                      border: "1px solid rgba(39, 174, 96, 0.2)",
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      width={16}
                                      height={16}
                                      style={{ marginRight: "5px" }}
                                    >
                                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM17.4571 9.45711L11 15.9142L6.79289 11.7071L8.20711 10.2929L11 13.0858L16.0429 8.04289L17.4571 9.45711Z" />
                                    </svg>
                                    Réglé
                                  </span>
                                )}
                              </p>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center" style={{ padding: "30px" }}>
                            Aucune donnée disponible
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-end align-items-center mt-4">
                    <div className="pagination-container">
                      <div className="pagination">
                        {invoicePageNumbers.map((pageNum, index) => (
                          <button
                            key={index}
                            className={`page-item ${pageNum === invoicePage ? "active" : ""}`}
                            onClick={() => goToInvoicePage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* </div> */}
        {/* </div> */}
      </main>

      {/* CSS supplémentaire pour les animations et styles spécifiques */}
      {/* Ajouter des styles améliorés pour les onglets */}
      <style jsx>{`
  .tabs-list {
    display: flex;
    background-color: var(--primary-lightest);
    border-bottom: 1px solid rgba(48, 86, 162, 0.1);
    position: relative;
    overflow: hidden;
  }
  
  .tab-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem 1.5rem;
    background: none;
    border: none;
    color: var(--primary);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    font-size: 1rem;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  
  .tab-button::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 3px;
    background-color: var(--primary);
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }
  
  .tab-button:hover {
    background-color: rgba(48, 86, 162, 0.05);
    color: var(--primary);
  }
  
  .tab-button.active {
    color: var(--primary);
    background-color: rgba(48, 86, 162, 0.1);
  }
  
  .tab-button.active::after {
    width: 80%;
  }
  
  .tab-button::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background-color: rgba(48, 86, 162, 0.1);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: all 0.5s ease;
    z-index: -1;
  }
  
  .tab-button:hover::before {
    width: 120%;
    height: 120%;
  }
  
  .tab-content {
    display: none;
    padding: 1.5rem;
    animation: fadeIn 0.5s ease;
    background-color: #ffffff;
    border-radius: 0 0 8px 8px;
  }
  
  .tab-content.active {
    display: block;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Styles améliorés pour les cartes */
  .chart-card {
    background-color: var(--white);
    border-radius: var(--border-radius);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(48, 86, 162, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;
    opacity: 0;
    transform: translateY(20px);
  }
  
  .chart-card.animate {
    animation: fadeInUp 0.5s ease-out forwards;
  }
  
  .chart-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
  
  /* Styles améliorés pour les en-têtes de carte */
  .chart-header {
    padding: 1.5rem 1.5rem 0.75rem;
    border-bottom: 1px solid rgba(48, 86, 162, 0.05);
  }
  
  .chart-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 0.25rem;
  }
  
  .chart-description {
    color: var(--text-light);
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }
  
  /* Styles améliorés pour les tableaux */
  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin-bottom: 1rem;
  }
  
  .data-table th {
    text-align: left;
    padding: 1rem 0.75rem;
    background-color: rgba(48, 86, 162, 0.05);
    border-bottom: 2px solid rgba(48, 86, 162, 0.1);
    color: var(--primary);
    font-weight: 600;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .data-table td {
    padding: 0.9rem 0.75rem;
    border-bottom: 1px solid rgba(48, 86, 162, 0.05);
    transition: all 0.2s ease;
  }
  
  .data-table tr:last-child td {
    border-bottom: none;
  }
  
  .data-table tr:hover td {
    background-color: rgba(48, 86, 162, 0.02);
  }
  
  /* Styles améliorés pour les boutons d'action */
  .export-action-button {
    padding: 0.6rem 1.2rem;
    background-color: var(--primary);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    box-shadow: 0 2px 5px rgba(48, 86, 162, 0.2);
  }
  
  .export-action-button:hover {
    background-color: #214293;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(48, 86, 162, 0.3);
  }
  
  .export-action-button:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(48, 86, 162, 0.2);
  }
  
  /* Styles améliorés pour les lignes de commande */
  .order-row {
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .order-row:hover {
    background-color: rgba(48, 86, 162, 0.05);
    transform: translateX(5px);
  }
  
  .order-row.active {
    background-color: #3656a1;
    color: white;
    transform: translateX(5px);
    box-shadow: 0 2px 8px rgba(54, 86, 161, 0.3);
  }
  
  .order-row.active td {
    border-bottom-color: #3656a1;
  }
  
  .order-row.active .product-name {
    color: white;
  }
  
  /* Styles améliorés pour les badges de statut */
  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
  }
  
  .status-badge.success {
    background-color: rgba(39, 174, 96, 0.1);
    color: #27ae60;
    border: 1px solid rgba(39, 174, 96, 0.2);
  }
  
  .status-badge:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  .status-badge svg {
    margin-right: 5px;
  }
  
  /* Styles améliorés pour le résumé de commande */
  .order-details {
    flex: 1;
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    border: 1px solid rgba(48, 86, 162, 0.1);
  }
  
  .order-details:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .summary-title {
    font-size: 1.2rem;
    color: var(--primary);
    margin-bottom: 1.25rem;
    text-align: center;
    font-weight: 600;
    position: relative;
  }
  
  .summary-title::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: var(--primary);
    border-radius: 3px;
  }
  
  .product-list {
    max-height: 400px;
    overflow-y: auto;
    padding-right: 5px;
  }
  
  .product-list::-webkit-scrollbar {
    width: 6px;
  }
  
  .product-list::-webkit-scrollbar-track {
    background: rgba(48, 86, 162, 0.05);
    border-radius: 10px;
  }
  
  .product-list::-webkit-scrollbar-thumb {
    background: rgba(48, 86, 162, 0.2);
    border-radius: 10px;
  }
  
  .product-list::-webkit-scrollbar-thumb:hover {
    background: rgba(48, 86, 162, 0.3);
  }
  
  .product-item {
    padding: 12px 0;
    border-bottom: 1px solid rgba(48, 86, 162, 0.1);
    transition: all 0.3s ease;
  }
  
  .product-item:hover {
    background-color: rgba(48, 86, 162, 0.02);
    transform: translateX(5px);
  }
  
  .product-item:last-child {
    border-bottom: none;
  }
  
  .product-name {
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--primary);
  }
  
  /* Styles améliorés pour la pagination */
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }
  
  .pagination {
    display: flex;
    gap: 8px;
  }
  
  .page-item {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid rgba(48, 86, 162, 0.2);
    background-color: white;
    color: var(--primary);
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
  }
  
  .page-item:hover {
    background-color: rgba(48, 86, 162, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  .page-item.active {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
    box-shadow: 0 2px 8px rgba(48, 86, 162, 0.3);
  }

  .commandes-line {
    z-index: 10;
    stroke-width: 3px !important;
  }

  .revenue-bar {
    opacity: 0.8;
  }

  .chart-container.hovered .commandes-line {
    stroke-width: 4px !important;
  }

  .custom-tooltip {
    background-color: white;
    border-radius: 5px;
    padding: 10px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(48, 86, 162, 0.1);
  }

  .tooltip-label {
    font-weight: bold;
    margin-bottom: 5px;
    color: #333;
  }

  .tooltip-value {
    display: flex;
    align-items: center;
    margin: 3px 0;
  }

/* Styles améliorés pour le résumé de commande */
.order-details {
  flex: 1;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid rgba(48, 86, 162, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.order-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(48, 86, 162, 0.1);
}

.order-summary-header h3 {
  margin: 0;
  color: #3056a2;
  font-size: 1.2rem;
  font-weight: 600;
}

.order-summary-id {
  background-color: #3056a2;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
}

.order-products-header {
  display: grid;
  grid-template-columns: 3fr 1fr 2fr 2fr;
  padding: 0.75rem 1.5rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(48, 86, 162, 0.1);
  font-weight: 600;
  color: #3056a2;
  font-size: 0.9rem;
}

.order-products-list {
  overflow-y: auto;
  max-height: 300px;
}

.order-product-item {
  display: grid;
  grid-template-columns: 3fr 1fr 2fr 2fr;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid rgba(48, 86, 162, 0.05);
  transition: background-color 0.2s ease;
}

.order-product-item:hover {
  background-color: rgba(48, 86, 162, 0.02);
}

.product-col-name {
  font-weight: 500;
  color: #333;
  padding-right: 1rem;
}

.product-col-qty {
  text-align: center;
  color: #555;
}

.product-col-price {
  text-align: right;
  color: #555;
}

.product-col-total {
  text-align: right;
  font-weight: 600;
  color: #333;
}

.order-summary-totals {
  margin-top: auto;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
  border-top: 1px solid rgba(48, 86, 162, 0.1);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  color: #555;
}

.summary-row.total {
  font-weight: 700;
  color: #3056a2;
  font-size: 1.1rem;
  border-top: 1px solid rgba(48, 86, 162, 0.1);
  padding-top: 0.75rem;
  margin-top: 0.5rem;
}

.order-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(48, 86, 162, 0.1);
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid rgba(48, 86, 162, 0.2);
  border-radius: 4px;
  color: #3056a2;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(.disabled) {
  background-color: #3056a2;
  color: white;
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.9rem;
  color: #666;
}

.order-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  color: #666;
  text-align: center;
}

.order-empty-state svg {
  margin-bottom: 1rem;
  opacity: 0.7;
}

.order-empty-state p {
  font-size: 1rem;
  margin: 0;
}

/* Styles pour la modal d'exportation */
.export-modal .modal-title {
  font-size: 1.5rem;
  color: #3056a2;
  font-weight: 600;
}

.export-options-container {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
}

.export-options {
  display: flex;
  justify-content: center;
  gap: 3rem;
}

.export-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  text-decoration: none;
}

.export-option:hover {
  transform: translateY(-5px);
}

.export-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-label {
  margin-top: 0.5rem;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.export-excel-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
}

.close-button {
  background-color: #ff9933;
  border: none;
  padding: 0.5rem 2rem;
  font-weight: 600;
  font-size: 1rem;
}

.close-button:hover {
  background-color: #ff8000;
}

/* Styles pour la barre de progression */
.progress-container {
  margin-top: 10px;
  width: 100%;
}

.progress-bar-wrapper {
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
}

.progress-bar {
  height: 100%;
  background-color: #28a745;
  border-radius: 10px;
  transition: width 0.5s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.progress-text {
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #666;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`}</style>
    </div>
  )
}

export default Performance
